<template>
    <!-- --open følger panelet, ikke fokus: temaer kan henge et slør eller en aktiv
         feltstil på den og beholde den når tastaturet er tatt ned (se onSuggestionsTouchMove). -->
    <div ref="root" class="bits-searchbar" :class="{ 'bits-searchbar--open': visible && hasContent }">
        <input
            ref="input"
            type="search"
            :value="text"
            @input="onInput(($event.target as HTMLInputElement).value)"
            @keydown.enter="submit"
            @keydown.down.prevent="cycle(1)"
            @keydown.up.prevent="cycle(-1)"
            @focus="onFocus"
            @blur="onBlur"
            placeholder="Hva leter du etter?"
            autocomplete="off"
        />
        <button type="button" @click="submit">Søk</button>
        <!-- Seksjonene ligger i hver sin wrapper slik at fullbredde-varianten kan stokke om
             på dem med CSS `order` (innhold før produkter, som i den gamle Clerk-visningen).
             I kompakt visning er wrapperen `display: contents`, så DOM-en oppfører seg som
             før for temaene som skinner søket selv. -->
        <div
            v-if="visible && hasContent"
            class="bits-searchbar__suggestions"
            :class="{ 'bits-searchbar__suggestions--fullwidth': isFullWidth }"
            @touchmove.passive="onSuggestionsTouchMove"
        >
            <div v-if="categories.length > 0" class="bits-searchbar__section bits-searchbar__section--categories">
                <div class="bits-searchbar__section-label">Kategorier</div>
                <div class="bits-searchbar__links">
                    <a
                        v-for="c in categories"
                        :key="'c' + c.id"
                        :href="c.url"
                        :title="categoryLabel(c)"
                        @mousedown.prevent="goTo(c.url)"
                    ><span
                        v-if="c.path && c.path.length > 0"
                        class="bits-searchbar__category-path"
                    >{{ c.path.join(' › ') }} › </span>{{ c.name }}</a>
                </div>
            </div>
            <div v-if="suggestionRow.length > 0" class="bits-searchbar__section bits-searchbar__section--suggestions">
                <div class="bits-searchbar__section-label">Søkeforslag</div>
                <div class="bits-searchbar__links">
                    <a
                        v-for="(t, i) in suggestionRow"
                        :key="'s' + t"
                        href="#"
                        :class="{ 'is-active': i === active }"
                        @mousedown.prevent="pick(t)"
                    ><strong v-if="matchedPart(t)">{{ matchedPart(t) }}</strong>{{ completionPart(t) }}</a>
                </div>
            </div>
            <div v-if="products.length > 0" class="bits-searchbar__section bits-searchbar__section--products">
                <div class="bits-searchbar__section-label">Produkter</div>
                <a
                    v-for="(p, i) in products"
                    :key="'p' + p.id"
                    class="bits-searchbar__product"
                    :href="p.url"
                    :data-productid="p.id"
                    @mousedown.prevent="openProduct(p, i)"
                >
                    <img v-if="p.imageUrl" :src="p.imageUrl" alt="" loading="lazy" />
                    <!-- Med Lipscore stables navn og stjerner i en egen boks. Uten står navnet
                         som før direkte i raden, så temaer som skinner raden ikke merker noe. -->
                    <span v-if="lipscore" class="bits-searchbar__product-info">
                        <span class="bits-searchbar__product-name">{{ p.name }}</span>
                        <span
                            class="lipscore-rating-small bits-searchbar__product-rating"
                            :data-ls-product-id="p.id"
                            :data-ls-product-name="p.name"
                            :data-ls-product-url="absoluteUrl(p.url)"
                        ></span>
                    </span>
                    <span v-else class="bits-searchbar__product-name">{{ p.name }}</span>
                    <!-- Tilbud: førprisen gjennomstreket foran tilbudsprisen, samme
                         rekkefølge som temaenes produktkort. Uten den leses tilbuds-
                         prisen som ordinærpris. -->
                    <span v-if="p.price || p.oldPrice" class="bits-searchbar__product-prices">
                        <span v-if="p.oldPrice" class="bits-searchbar__product-oldprice">{{ p.oldPrice }}</span>
                        <span v-if="p.price" class="bits-searchbar__product-price">{{ p.price }}</span>
                    </span>
                </a>
            </div>
            <div v-if="pages.length > 0" class="bits-searchbar__section bits-searchbar__section--pages">
                <div class="bits-searchbar__section-label">{{ contentHeading }}</div>
                <a
                    v-for="(pg, i) in pages"
                    :key="'pg' + pg.url + i"
                    class="bits-searchbar__page"
                    :href="pg.url"
                    @mousedown.prevent="goTo(pg.url)"
                >
                    <!-- Samme radoppsett som produktene over: bilderamma tegnes også
                         uten bilde, ellers står titlene i to ulike venstremarger. -->
                    <span class="bits-searchbar__page-thumb">
                        <img v-if="pg.imageUrl" :src="pg.imageUrl" alt="" loading="lazy" />
                    </span>
                    <span class="bits-searchbar__page-title">{{ pg.title }}</span>
                    <small v-if="pg.source" class="bits-searchbar__page-source">{{ pg.source }}</small>
                </a>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { autoComplete, trackInsight, searchShape, type AutoCompleteProductDto, type CategorySuggestionDto, type ExternalResultDto, type PopularSearchDto } from './api';

const props = defineProps<{
    initialQuery?: string;
    layout?: string;
    contentHeading?: string;
    lipscore?: boolean;
}>();

// Fullbredde speiler oppsettet butikkene hadde med Clerk: panelet dekker sidebredden,
// innholdstreffene ligger over produktene, og begge vises som kort i rutenett.
const isFullWidth = computed(() => props.layout === 'fullwidth');
const contentHeading = computed(() => props.contentHeading || 'Innholdssider');

const text = ref<string>(props.initialQuery ?? '');
const tokens = ref<Array<{ token: string; count: number }>>([]);
const categories = ref<CategorySuggestionDto[]>([]);
const products = ref<AutoCompleteProductDto[]>([]);
const popular = ref<PopularSearchDto[]>([]);
const pages = ref<ExternalResultDto[]>([]);
const visible = ref<boolean>(false);
const active = ref<number>(-1);
const hasContent = computed(() =>
    tokens.value.length > 0 || products.value.length > 0 ||
    popular.value.length > 0 || categories.value.length > 0 ||
    pages.value.length > 0);

// Full sti som tooltip/skjermleser-tekst: lenketeksten viser den samme stien, men der
// er forfedrene dempet, og en kunde som holder musa over vil ha hele navnet i klartekst.
function categoryLabel(c: CategorySuggestionDto): string {
    return c.path && c.path.length > 0 ? [...c.path, c.name].join(' › ') : c.name;
}

// Fet utheving av delen kunden alt har skrevet («**adidas** crw», som Clerk):
// forslaget deles i matchet prefiks og fullføring. Matcher ikke forslaget det
// skrevne (populære søk kan avvike), vises hele uthevet-fritt.
function matchedPart(suggestion: string): string {
    const q = text.value.trim().toLowerCase();
    return q && suggestion.toLowerCase().startsWith(q) ? suggestion.slice(0, q.length) : '';
}

function completionPart(suggestion: string): string {
    return suggestion.slice(matchedPart(suggestion).length);
}

// Én «Søkeforslag»-rad som i gammel design: populære søk først, så ordforslag.
const suggestionRow = computed(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const k of [...popular.value.map(p => p.keyword), ...tokens.value.map(t => t.token)]) {
        const key = k.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(k);
        if (out.length >= 6) break;
    }
    return out;
});
let pending: AbortController | null = null;
let debounceId: number | null = null;

// Hurtigsøket spør for hvert tastetrykk. Som ett søk telles det kunden ble stående ved:
// ingen ny bokstav på 1,5 sekunder, eller et valg i forslagene.
let insightTimer: number | null = null;
let lastTracked = '';

function trackSearch(q: string): void {
    if (insightTimer) window.clearTimeout(insightTimer);
    insightTimer = null;
    const key = q.trim().toLowerCase();
    if (key.length < 2 || key === lastTracked) return;
    lastTracked = key;
    trackInsight('search_performed', {
        source: 'autocomplete',
        results: products.value.length,
        categories: categories.value.length,
        zero_results: products.value.length === 0 && categories.value.length === 0,
        ...searchShape(q),
    });
}

function onInput(value: string): void {
    text.value = value;
    if (debounceId) window.clearTimeout(debounceId);
    debounceId = window.setTimeout(() => fetchSuggestions(value), 120);
}

async function fetchSuggestions(q: string): Promise<void> {
    // Tom/kort q: hent kun populære søk (vises på fokus før man har skrevet noe).
    const effective = q.trim().length < 2 ? '' : q;
    pending?.abort();
    const ctrl = new AbortController();
    pending = ctrl;
    try {
        const r = await autoComplete(effective, ctrl.signal);
        if (pending !== ctrl) return;
        tokens.value = r.tokens ?? [];
        categories.value = r.categories ?? [];
        products.value = r.products ?? [];
        popular.value = r.popularSearches ?? [];
        pages.value = r.pages ?? [];
        visible.value = hasContent.value;
        active.value = -1;
        if (insightTimer) window.clearTimeout(insightTimer);
        if (effective) insightTimer = window.setTimeout(() => trackSearch(effective), 1500);
    } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.error('autocomplete failed', e);
    }
}

function onFocus(): void {
    if (hasContent.value) {
        visible.value = true;
        return;
    }
    void fetchSuggestions(text.value);
}

function goTo(url: string): void {
    trackSearch(text.value);
    window.location.href = url;
}

function openProduct(p: AutoCompleteProductDto, index: number): void {
    trackSearch(text.value);
    trackInsight('product_clicked', { product_id: p.id, list: 'autocomplete', position: index + 1 });
    window.location.href = p.url;
}

function cycle(direction: number): void {
    if (suggestionRow.value.length === 0) return;
    let next = active.value + direction;
    if (next < 0) next = suggestionRow.value.length - 1;
    if (next >= suggestionRow.value.length) next = 0;
    active.value = next;
}

function pick(token: string): void {
    text.value = token;
    visible.value = false;
    submit();
}

function submit(): void {
    const q = (active.value >= 0 ? suggestionRow.value[active.value] : text.value).trim();
    if (!q) return;
    trackSearch(text.value);
    window.location.href = `/bits-search?q=${encodeURIComponent(q)}`;
}

// Mobil: tastaturet dekker mye av panelet, og det går bare ned når feltet mister fokus --
// men blur lukker panelet. Når kunden drar i panelet for å se flere treff, tas fokus fra
// feltet uten at panelet lukkes. Det lukkes i stedet ved trykk utenfor søkebaren
// (onDocumentPointerDown), og kommer tilbake med tastaturet ved trykk i feltet (onFocus).
const root = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
let keepOpenOnBlur = false;

function onSuggestionsTouchMove(): void {
    if (!input.value || document.activeElement !== input.value) return;
    keepOpenOnBlur = true;
    input.value.blur();
}

function onBlur(): void {
    if (keepOpenOnBlur) {
        keepOpenOnBlur = false;
        return;
    }
    window.setTimeout(() => { visible.value = false; }, 150);
}

function onDocumentPointerDown(e: PointerEvent): void {
    if (!visible.value || !root.value) return;
    if (!root.value.contains(e.target as Node)) visible.value = false;
}

onMounted(() => document.addEventListener('pointerdown', onDocumentPointerDown));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointerDown));

watch(() => props.initialQuery, (v) => { text.value = v ?? ''; });

// Lipscore vil ha absolutt URL; API-et leverer rotrelative produktlenker.
function absoluteUrl(url: string): string {
    try { return new URL(url, window.location.origin).href; } catch { return url; }
}

// Forslagspanelet er v-if: det bygges på nytt hver gang det åpnes, ikke bare når treffene
// endres. Begge deler gir nye widget-spans, derfor watcher vi på synlighet og treff.
// initWidgets fyller bare widgets som ikke er fylt, så gjentatte kall er ufarlige.
// Eventet sendes uansett Lipscore, for temaer som vil pynte på forslagene etter rendring.
// flush: 'post' kjører etter at Vue har patchet DOM-en, så spansene finnes når vi kaller.
watch([visible, products], () => {
    if (!visible.value || !hasContent.value) return;
    document.dispatchEvent(new CustomEvent('bits-search:suggestions-rendered', {
        detail: { productIds: products.value.map(p => p.id) }
    }));
    if (props.lipscore) {
        (window as unknown as { lipscore?: { initWidgets?: () => void } }).lipscore?.initWidgets?.();
    }
}, { flush: 'post' });
</script>
